import moment from 'moment';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Button, Col, Container, Row, Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { EmailRegex, SUBMIT_STATUS, USA_STATES } from '../../../../helpers/constants';
import { isClient } from '../../../../helpers/global';
import { onScrollToErrorPosition } from '../../../../helpers/ui';
import useAppChoices from '../../../../hooks/useAppChoices';
import useCountyCityOptions from '../../../../hooks/useCountyCityOptions';
import { isArray } from 'lodash';
import { validateEmail } from '../../../../helpers/global';
import {
  NO_REQUESTED_BY_USERS_MESSAGE,
  REQUESTED_BY_REQUIRED_MESSAGE,
  requestedByApplies,
  requestedByError,
  savedRequestedById
} from '../../../../helpers/requestedBy';
import RequestedByField, { useRequestedByField } from '../../../common/RequestedByField';

let initialFormFieldError = [
  { key: 'individual.name', isNotValid: false, type: 'string' },
  { key: 'individual.email', isNotValid: false, type: 'email' },
  { key: 'fileName', isNotValid: false, type: 'string' },
  { key: 'buyerName', isNotValid: false, type: 'string' },
  { key: 'sellerName', isNotValid: false, type: 'string' },
  { key: 'propertyAddress', isNotValid: false, type: 'string' },
  { key: 'city', isNotValid: false, type: 'string' },
  { key: 'county', isNotValid: false, type: 'string' },
  { key: 'state', isNotValid: false, type: 'string' },
  { key: 'zipCode', isNotValid: false, type: 'string' },
  { key: 'closingDate', isNotValid: false, type: 'string' },
  { key: 'sellerEmail', isNotValid: false, type: 'email' },
  { key: 'buyerEmail', isNotValid: false, type: 'email' },
  { key: 'folioNumbers', type: [{ key: 'name', isNotValid: false }] }
];

// function to update whether the fields are valid or not
const checkIsValid = (formData, fieldError = [], context = {}) => {
  let error = false;
  return {
    result: fieldError.map(field => {
      const key = field.key;
      const type = field.type;

      if (key === 'individual.name' || key === 'individual.email') {
        const [individualKey, individualSubKey] = key.split('.');
        const individualValue = formData[individualKey][individualSubKey];
        // Only validate if isIndividualService is true
        if (formData.isIndividualService) {
          if (type === 'string') {
            if (individualValue === '' || individualValue === null || individualValue === undefined) {
              error = true;
              return { key, isNotValid: true, type };
            } else {
              return { key, isNotValid: false, type };
            }
          } else if (type === 'email') {
            if (individualValue !== '' && individualValue !== null && individualValue !== undefined) {
              if (!EmailRegex.test(individualValue)) {
                error = true;
                return { key, isNotValid: true, type, isEmailError: true };
              } else {
                return { key, isNotValid: false, type };
              }
            } else {
              error = true;
              return { key, isNotValid: true, type };
            }
          }
        } else {
          return { key, isNotValid: false, type };
        }
      }

      if (key === 'requestedBy') {
        const organization = (context.organizations || []).find(item => item._id === formData.organization);
        const message = requestedByError({
          organization,
          isIndividual: formData.isIndividualService,
          users: context.users,
          requestedBy: formData.requestedBy
        });
        if (message) {
          error = true;
          return { key, isNotValid: true, type: 'string' };
        }
        return { key, isNotValid: false, type: 'string' };
      }

      // Handle regular fields
      const value = formData[key];
      if (type === 'string') {
        if (value === '' || value === null || value === undefined) {
          error = true;
          return { key, isNotValid: true, type };
        } else {
          return { key, isNotValid: false, type };
        }
      } else if (type === 'email') {
        if (value !== '' && value !== null && value !== undefined) {
          if (!EmailRegex.test(value)) {
            error = true;
            return { key, isNotValid: true, type };
          } else {
            return { key, isNotValid: false, type };
          }
        } else {
          return { key, isNotValid: false, type };
        }
      } else {
        return {
          key,
          type: field.type.map((f, index) => {
            const key = f.key;
            const value = formData.folioNumbers[index][key];
            if (value === '' || value === null || value === undefined) {
              error = true;
              return { key, isNotValid: true };
            } else {
              return { key, isNotValid: false };
            }
          })
        };
      }
    }),
    error
  };
};

// function to update the field on change
const updateOnChangeErrorField = (fieldError = [], key) => {
  return fieldError.map(field => {
    if (field.key === key) {
      if (key === 'folioNumbers') {
        let fieldType = [...field.type];
        fieldType[0] = { key: 'name', isNotValid: false };
        return { key, type: fieldType };
      }
      return { key, isNotValid: false, type: field.type };
    } else {
      return field;
    }
  });
};

const ServiceForm = ({ onSubmit, isEditingService, loading, onChangeSelectedOrganization, isPublic, onClose }) => {
  const role = useMemo(() => localStorage.getItem('user-role'), []);
  const [formFieldError, setFormFieldError] = useState(
    !isPublic && !isClient(role)
      ? [...initialFormFieldError, { key: 'requestedBy', isNotValid: false, type: 'string' }]
      : [...initialFormFieldError].filter(v => v.key !== 'organization')
  );
  const [status, setStatus] = useState(SUBMIT_STATUS.IDLE);
  const orgs = useAppChoices('organizations');
  const users = useAppChoices('users');
  const organizations = isEditingService?._id ? orgs : orgs.filter(o => o.isEnabled);
  const propertyTypeValues = useAppChoices('Property Type') || [];
  const propertyTypeOptions = propertyTypeValues.map(v => ({
    value: v,
    label: v
  }));
  const { cities, counties, getCountyForCity } = useCountyCityOptions();

  const [formData, setFormData] = useState({
    ...(!isPublic &&
      !isClient(role) && { organization: !isClient(role) ? organizations[0]?._id || undefined || '' : undefined }),
    isIndividualService: false,
    individual: {
      name: '',
      email: '',
      phoneNumber: ''
    },
    fileName: '',
    buyerName: '',
    buyerPhone: '',
    buyerEmail: '',
    sellerName: '',
    sellerPhone: '',
    sellerEmail: '',
    propertyAddress: '',
    city: '',
    county: '',
    state: 'FLORIDA',
    zipCode: '',
    legalDescription: '',
    folioNumbers: [{ name: '' }],
    propertyType: '',
    modificationAndSpecialInstructions: '',
    closingDate: '',
    propertyAppraiserAddress: '',
    requestedBy: ''
  });

  useEffect(() => {
    if (isEditingService?._id) {
      setFormData({
        organization: isEditingService?.organization?._id || organizations[0]?._id || '',
        requestedBy: savedRequestedById(isEditingService?.requestedBy),
        isIndividualService: isEditingService?.isIndividualService || false,
        individual: {
          name: isEditingService?.individual?.name || '',
          email: isEditingService?.individual?.email || '',
          phoneNumber: isEditingService?.individual?.phoneNumber || ''
        },
        buyerEmail: isEditingService?.buyerEmail || '',
        buyerName: isEditingService?.buyerName || '',
        buyerPhone: isEditingService?.buyerPhone || '',
        city: isEditingService?.city || '',
        county: isEditingService?.county || '',
        fileName: isEditingService?.fileName || '',
        folioNumbers: isEditingService?.folioNumbers?.map((v, index) => ({
          name: isEditingService?.folioNumbers[index]
        })) || [{ name: '' }],
        legalDescription: isEditingService?.legalDescription || '',
        propertyType: isEditingService?.propertyType || '',
        modificationAndSpecialInstructions: isEditingService?.modificationAndSpecialInstructions || '',
        closingDate:
          (isEditingService?.closingDate && moment(isEditingService?.closingDate).format('YYYY-MM-DD')) || '',
        propertyAddress: isEditingService?.propertyAddress || '',
        sellerEmail: isEditingService?.sellerEmail || '',
        sellerName: isEditingService?.sellerName || '',
        sellerPhone: isEditingService?.sellerPhone || '',
        state: isEditingService?.state || '',
        zipCode: isEditingService?.zipCode || '',
        propertyAppraiserAddress: isEditingService?.propertyAppraiserAddress || ''
      });
      isEditingService?.folioNumbers && setFolioNumbersFieldError([...isEditingService?.folioNumbers]);
    }

    return () => {
      initialFormFieldError = [
        { key: 'individual.name', isNotValid: false, type: 'string' },
        { key: 'individual.email', isNotValid: false, type: 'email' },
        { key: 'fileName', isNotValid: false, type: 'string' },
        { key: 'buyerName', isNotValid: false, type: 'string' },
        { key: 'sellerName', isNotValid: false, type: 'string' },
        { key: 'propertyAddress', isNotValid: false, type: 'string' },
        { key: 'city', isNotValid: false, type: 'string' },
        { key: 'county', isNotValid: false, type: 'string' },
        { key: 'state', isNotValid: false, type: 'string' },
        { key: 'zipCode', isNotValid: false, type: 'string' },
        { key: 'folioNumbers', type: [{ key: 'name', isNotValid: false }] },
        { key: 'closingDate', isNotValid: false, type: 'string' },
        { key: 'sellerEmail', isNotValid: false, type: 'email' },
        { key: 'buyerEmail', isNotValid: false, type: 'email' }
      ];
    };
  }, [isEditingService?._id]);

  const setFolioNumbersFieldError = folioNumbers => {
    if (
      folioNumbers &&
      folioNumbers?.length > 1 &&
      formFieldError.find(e => e.key === 'folioNumbers').type.length < folioNumbers?.length
    ) {
      folioNumbers
        .splice(1, folioNumbers.length)
        .forEach(() =>
          formFieldError.find(e => e.key === 'folioNumbers').type.push({ key: 'name', isNotValid: false })
        );
      setFormFieldError([...formFieldError]);
    }
  };

  const onHandleClickSubmit = async () => {
    try {
      setStatus(SUBMIT_STATUS.LOADING);
      const { result, error } = checkIsValid(formData, formFieldError, { users, organizations });
      if (error) {
        setFormFieldError(result);
        const field = result.find(e => e.isNotValid === true || (isArray(e.type) && e.type.find(f => f.isNotValid)));
        onScrollToErrorPosition(field?.key);
        throw error;
      } else {
        // Prepare the final form data based on isIndividualService
        let finalFormData = { ...formData };
        if (formData.isIndividualService) {
          // Include individual fields and remove organization
          const { organization, ...formDataWithoutOrg } = finalFormData;
          finalFormData = {
            ...formDataWithoutOrg,
            individual: {
              name: formData.individual.name,
              email: formData.individual.email,
              phoneNumber: formData.individual.phoneNumber
            }
          };
        } else {
          // Include organization and remove individual fields
          const { individual, ...formDataWithoutIndividual } = finalFormData;
          finalFormData = {
            ...formDataWithoutIndividual,
            organization: formData.organization
          };
        }
        const selectedOrganization = organizations.find(org => org._id === finalFormData.organization);
        const staffCanSetRequestedBy = !isPublic && !isClient(role);
        if (!staffCanSetRequestedBy) {
          delete finalFormData.requestedBy;
        } else if (!requestedByApplies(selectedOrganization, finalFormData.isIndividualService)) {
          finalFormData.requestedBy = null;
        }
        await onSubmit({
          ...finalFormData,
          folioNumbers: [formData?.['folio'] || formData?.folioNumbers[0]?.name]
        });
        setStatus(SUBMIT_STATUS.SUCCESS);
      }
    } catch (error) {
      setStatus(SUBMIT_STATUS.FAILED);
      setTimeout(() => {
        setStatus(SUBMIT_STATUS.IDLE);
      }, 2000);
    }
  };

  const onFormDataChange = (field, e) => {
    if (e && e?.target) {
      const updatedFormData = { ...formData };
      if (field === 'folioNumbers') {
        updatedFormData[field][0].name = e.target.value;
      } else if (field === 'individual.name') {
        updatedFormData.individual = { ...formData.individual, name: e.target.value };
      } else if (field === 'individual.email') {
        updatedFormData.individual = { ...formData.individual, email: e.target.value };
      } else if (field === 'individualPhoneNumber') {
        updatedFormData.individual = { ...formData.individual, phoneNumber: e.target.value };
      } else {
        updatedFormData[field] = e.target.value;
      }
      if (field === 'organization') {
        // this onchange is used when we are creating the service and changing the organization changes the porcessing fee of the orders
        updatedFormData.requestedBy = '';
        onChangeSelectedOrganization && onChangeSelectedOrganization(orgs.find(o => o?._id === e.target.value));
      }
      if (field === 'city') {
        const county = getCountyForCity(e.target.value);
        if (county) {
          updatedFormData['county'] = county;
        }
      }
      setFormData(updatedFormData);
      setFormFieldError(updateOnChangeErrorField(formFieldError, field));
    }
  };

  const onToggleIsIndividualService = value => {
    setFormData(prev => ({ ...prev, isIndividualService: value }));
  };

  useEffect(() => {
    if (!formData.requestedBy) return;
    setFormFieldError(prev => {
      const field = prev.find(item => item.key === 'requestedBy');
      if (!field?.isNotValid) return prev;
      return updateOnChangeErrorField(prev, 'requestedBy');
    });
  }, [formData.requestedBy]);

  const setRequestedBy = useCallback(id => {
    setFormData(prev => {
      if ((prev.requestedBy || '') === (id || '')) return prev;
      return { ...prev, requestedBy: id || '' };
    });
    setFormFieldError(prev => updateOnChangeErrorField(prev, 'requestedBy'));
  }, []);

  const { applies: showRequestedBy, eligible: requestedByUsers } = useRequestedByField({
    users,
    organizations,
    organizationId: formData.organization,
    isIndividual: formData.isIndividualService,
    enabled: !isPublic && !isClient(role),
    setFormData
  });

  const isFieldNotValid = (key, index) => {
    const field = formFieldError.find(f => f.key === key);
    if (field?.type === 'string' || field?.type === 'email') {
      return field.isNotValid;
    } else {
      return field?.type[index].isNotValid;
    }
  };

  return (
    <Container fluid className="m-0 relative">
      {/* First Row */}
      <Row>
        {!isPublic && !isClient(role) && (
          <>
            {!formData?.isIndividualService && (
              <Col md={4} xs={12} className="align-self-end">
                <Form.Group>
                  <Form.Label className="font-weight-bold">
                    <div className="d-flex align-items-center ">
                      <span>
                        Organization<span className="text-danger">*</span>
                      </span>

                      <div className="ml-4 toggle-link" onClick={() => onToggleIsIndividualService(true)}>
                        Is Individual?
                      </div>
                    </div>
                  </Form.Label>
                  <Select
                    id="organization"
                    name="organization"
                    classNamePrefix="select"
                    required
                    isSearchable
                    onChange={selectedOrg =>
                      onFormDataChange('organization', { target: { value: selectedOrg?._id || '' } })
                    }
                    isClearable
                    className="select-organization"
                    value={organizations.find(org => org._id === formData.organization) || null}
                    options={organizations}
                    getOptionLabel={option => option.name}
                    getOptionValue={option => option._id}
                  />
                  {isFieldNotValid('organization') && <p className="xsmallFont text-rush">This is a required Field</p>}
                </Form.Group>
              </Col>
            )}
            {showRequestedBy && (
              <Col md={4} xs={12}>
                <RequestedByField
                  eligible={requestedByUsers}
                  value={formData.requestedBy || ''}
                  onChange={setRequestedBy}
                  error={
                    isFieldNotValid('requestedBy')
                      ? requestedByUsers.length
                        ? REQUESTED_BY_REQUIRED_MESSAGE
                        : NO_REQUESTED_BY_USERS_MESSAGE
                      : ''
                  }
                />
              </Col>
            )}
          </>
        )}
        <Col md={!isPublic && !isClient(role) ? 4 : 6} xs={12} className="align-self-end">
          <Form.Group className="midFont">
            <Form.Label className="font-weight-bold">
              File Name/Number<span className="text-danger">*</span>
            </Form.Label>
            <input
              id="fileName"
              onChange={e => onFormDataChange('fileName', e)}
              type="text"
              value={formData.fileName}
              className={`form-control form-control-sm ${isFieldNotValid('fileName') && 'input-invalid'}`}
            />
            {isFieldNotValid('fileName') && <p className="xsmallFont text-rush">This is a required Field</p>}
          </Form.Group>
        </Col>
        <Col className="align-self-end" md={!isPublic && !isClient(role) ? 4 : 6} xs={12}>
          <Form.Group className="midFont">
            <Form.Label className="font-weight-bold">
              Folio #<span className="text-danger">*</span>
            </Form.Label>
            <input
              id="folioNumbers"
              onChange={e => onFormDataChange('folioNumbers', e)}
              type="text"
              value={formData?.folioNumbers[0]?.name || ''}
              className={`form-control form-control-sm ${isFieldNotValid('folioNumbers', 0) && 'input-invalid'}`}
            />
            {isFieldNotValid('folioNumbers', 0) && <p className="xsmallFont text-rush">This is a required Field</p>}
          </Form.Group>
        </Col>
      </Row>

      {/* Is Individual Service Checkbox and Fields */}
      {!isPublic && !isClient(role) && (
        <Row className="align-items-end mt-1">
          {formData?.isIndividualService && (
            <>
              <Col md={4} xs={12} className="align-self-end">
                <Form.Group className="midFont">
                  <Form.Label className="font-weight-bold">
                    <div className="d-flex align-items-center ">
                      <span>
                        Individual Name<span className="text-danger">*</span>
                      </span>

                      <div className="ml-4 toggle-link" onClick={() => onToggleIsIndividualService(false)}>
                        Is Organization?
                      </div>
                    </div>
                  </Form.Label>
                  <input
                    placeholder="Individual Name"
                    required
                    id="individual.name"
                    name="individual.name"
                    value={formData?.individual?.name || ''}
                    onChange={e => onFormDataChange('individual.name', e)}
                    type="text"
                    className={`form-control form-control-sm ${isFieldNotValid('individual.name') && 'input-invalid'}`}
                  />
                  {isFieldNotValid('individual.name') && (
                    <p className="xsmallFont text-rush">This is a required Field</p>
                  )}
                </Form.Group>
              </Col>
              <Col md={4} xs={12} className="align-self-end">
                <Form.Group className="midFont">
                  <Form.Label className="font-weight-bold">
                    Individual Email<span className="text-danger">*</span>
                  </Form.Label>
                  <input
                    placeholder="Email"
                    required
                    id="individual.email"
                    name="individual.email"
                    value={formData?.individual?.email || ''}
                    onChange={e => onFormDataChange('individual.email', e)}
                    type="email"
                    className={`form-control form-control-sm ${isFieldNotValid('individual.email') && 'input-invalid'}`}
                  />
                  {isFieldNotValid('individual.email') && (
                    <p className="xsmallFont text-rush">
                      {validateEmail(formData?.individual?.email) ? 'This is a required Field' : 'Email is not valid'}
                    </p>
                  )}
                </Form.Group>
              </Col>
              <Col md={4} xs={12} className="align-self-end">
                <Form.Group className="midFont">
                  <Form.Label className="font-weight-bold">Individual Phone Number</Form.Label>
                  <input
                    placeholder="Phone Number"
                    id="individualPhoneNumber"
                    name="individualPhoneNumber"
                    value={formData?.individual?.phoneNumber || ''}
                    onChange={e => onFormDataChange('individualPhoneNumber', e)}
                    type="tel"
                    className="form-control form-control-sm"
                  />
                </Form.Group>
              </Col>
            </>
          )}
        </Row>
      )}
      {/* Second Row */}
      <Row>
        <Col md={3}>
          <Form.Group className="midFont">
            <Form.Label className="font-weight-bold">
              Property Address<span className="text-danger">*</span>
            </Form.Label>
            <input
              id="propertyAddress"
              onChange={e => onFormDataChange('propertyAddress', e)}
              type="text"
              value={formData.propertyAddress}
              className={`form-control form-control-sm ${isFieldNotValid('propertyAddress') && 'input-invalid'}`}
            />
            {isFieldNotValid('propertyAddress') && <p className="xsmallFont text-rush">This is a required Field</p>}
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group className="midFont">
            <Form.Label className="font-weight-bold">Property Type</Form.Label>
            <Form.Control
              as={'select'}
              id="propertyType"
              value={formData?.propertyType}
              onChange={e => onFormDataChange('propertyType', e)}
              className="form-control form-control-sm"
              placeholder="Select Property Type"
            >
              {[{ value: '', label: 'Select One...' }, ...propertyTypeOptions].map(({ value, label }) => (
                <option value={value}>{label.toUpperCase()}</option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group className="midFont">
            <Form.Label className="font-weight-bold">
              City<span className="text-danger">*</span>
            </Form.Label>
            <input
              id="city"
              type="text"
              className={`form-control form-control-sm ${isFieldNotValid('city') && 'input-invalid'}`}
              as="input"
              list="city-list"
              value={formData.city}
              onChange={e => onFormDataChange('city', e)}
            ></input>
            <datalist id="city-list">
              {cities.map(c => (
                <option value={c} key={c + String(Math.random())}></option>
              ))}
            </datalist>
            {isFieldNotValid('city') && <p className="xsmallFont text-rush">This is a required Field</p>}
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group className="midFont">
            <Form.Label className="font-weight-bold">
              County<span className="text-danger">*</span>
            </Form.Label>
            <input
              id="county"
              type="text"
              className={`form-control form-control-sm ${isFieldNotValid('county') && 'input-invalid'}`}
              as="input"
              list="county-list"
              value={formData.county}
              onChange={e => onFormDataChange('county', e)}
            ></input>
            <datalist id="county-list">
              {counties.map(c => (
                <option value={c}></option>
              ))}
            </datalist>
            {isFieldNotValid('county') && <p className="xsmallFont text-rush">This is a required Field</p>}
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group className="midFont">
            <Form.Label className="font-weight-bold">
              State<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as={'select'}
              id="state"
              value={formData?.state}
              onChange={e => onFormDataChange('state', e)}
              className={`form-control form-control-sm ${isFieldNotValid('state') && 'input-invalid'}`}
            >
              {USA_STATES.map(({ name }) => (
                <option value={name}>{name}</option>
              ))}
            </Form.Control>
            {isFieldNotValid('state') && <p className="xsmallFont text-rush">This is a required Field</p>}
          </Form.Group>
        </Col>
        <Col md={1}>
          <Form.Group className="midFont">
            <Form.Label className="font-weight-bold">
              Zip Code<span className="text-danger">*</span>
            </Form.Label>
            <input
              id="zipCode"
              onChange={e => onFormDataChange('zipCode', e)}
              type="text"
              value={formData.zipCode}
              className={`form-control form-control-sm ${isFieldNotValid('zipCode') && 'input-invalid'}`}
            />
            {isFieldNotValid('zipCode') && <p className="xsmallFont text-rush">This is a required Field</p>}
          </Form.Group>
        </Col>
      </Row>

      {/* Third Row */}

      <Row>
        <Col md={4}>
          <Form.Group className="midFont">
            <Form.Label className="font-weight-bold">
              Buyer Name<span className="text-danger">*</span>
            </Form.Label>
            <input
              id="buyerName"
              onChange={e => onFormDataChange('buyerName', e)}
              type="text"
              value={formData.buyerName}
              className={`form-control form-control-sm ${isFieldNotValid('buyerName') && 'input-invalid'}`}
            />
            {isFieldNotValid('buyerName') && <p className="xsmallFont text-rush">This is a required Field</p>}
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="midFont">
            <Form.Label className="font-weight-bold">Buyer Phone Number</Form.Label>
            <input
              id="buyerPhone"
              onChange={e => onFormDataChange('buyerPhone', e)}
              type="text"
              value={formData.buyerPhone}
              className={`form-control form-control-sm`}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="midFont">
            <Form.Label className="font-weight-bold">Buyer Email</Form.Label>
            <input
              id="buyerEmail"
              onChange={e => onFormDataChange('buyerEmail', e)}
              type="text"
              value={formData.buyerEmail}
              className={`form-control form-control-sm ${isFieldNotValid('buyerEmail') && 'input-invalid'}`}
            />
            {isFieldNotValid('buyerEmail') && <p className="xsmallFont text-rush">Not a valid email</p>}
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <Form.Group className="midFont">
            <Form.Label className="font-weight-bold">
              Seller Name<span className="text-danger">*</span>
            </Form.Label>
            <input
              id="sellerName"
              onChange={e => onFormDataChange('sellerName', e)}
              type="text"
              value={formData.sellerName}
              className={`form-control form-control-sm ${isFieldNotValid('sellerName') && 'input-invalid'}`}
            />
            {isFieldNotValid('sellerName') && <p className="xsmallFont text-rush">This is a required Field</p>}
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="midFont">
            <Form.Label className="font-weight-bold">Seller Phone Number</Form.Label>
            <input
              onChange={e => onFormDataChange('sellerPhone', e)}
              type="text"
              value={formData.sellerPhone}
              className={`form-control form-control-sm `}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="midFont">
            <Form.Label className="font-weight-bold">Seller Email</Form.Label>
            <input
              id="sellerEmail"
              onChange={e => onFormDataChange('sellerEmail', e)}
              type="text"
              value={formData.sellerEmail}
              className={`form-control form-control-sm ${isFieldNotValid('sellerEmail') && 'input-invalid'}`}
            />
            {isFieldNotValid('sellerEmail') && <p className="xsmallFont text-rush">Not a valid email</p>}
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6} xs={12}>
          <Form.Group className="midFont">
            <Form.Label className="font-weight-bold">Modifications / Special Instruction</Form.Label>
            <textarea
              onChange={e => onFormDataChange('modificationAndSpecialInstructions', e)}
              className="form-control form-control-sm mt-1"
              rows="3"
              value={formData.modificationAndSpecialInstructions}
            ></textarea>
          </Form.Group>
        </Col>

        <Col md={6} xs={12}>
          <Form.Group className="midFont">
            <Form.Label className="font-weight-bold">Legal Description</Form.Label>
            <textarea
              id="legalDescription"
              onChange={e => onFormDataChange('legalDescription', e)}
              className="form-control form-control-sm mt-1"
              rows="3"
              value={formData.legalDescription}
            ></textarea>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="midFont">
            <Form.Label>Property Appraiser Address</Form.Label>
            <input
              id="propertyAppraiserAddress"
              onChange={e => onFormDataChange('propertyAppraiserAddress', e)}
              type="text"
              value={formData.propertyAppraiserAddress}
              className={`form-control form-control-sm ${isFieldNotValid('propertyAppraiserAddress') &&
                'input-invalid'}`}
            />
            {isFieldNotValid('propertyAppraiserAddress') && (
              <p className="xsmallFont text-rush">This is a required Field</p>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="midFont">
            <Form.Label>
              Closing Date<span className="text-danger">*</span>
            </Form.Label>
            <input
              id="closingDate"
              onChange={e => onFormDataChange('closingDate', e)}
              type="date"
              value={formData.closingDate}
              className={`form-control form-control-sm ${isFieldNotValid('closingDate') && 'input-invalid'}`}
            />
            {isFieldNotValid('closingDate') && <p className="xsmallFont text-rush">This is a required Field</p>}
          </Form.Group>
        </Col>
      </Row>
      <div
        className="d-flex justify-content-end align-items-end "
        style={{ position: 'fixed', bottom: '10px', right: '10px', zIndex: '1' }}
      >
        <Button
          variant="success"
          className="d-inline-flex align-items-center justify-content-center"
          disabled={loading}
          size="lg"
          onClick={onHandleClickSubmit}
        >
          {loading && <Spinner size="sm" animation="border" className="mr-1" />}
          {isEditingService?._id ? 'Submit' : 'Finish'}
        </Button>
      </div>
    </Container>
  );
};

export default ServiceForm;
